import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  Download,
  RotateCcw,
} from 'lucide-react';
import { apiUrl } from '../../services/apiClient';
import LogicTree from './LogicTree';
import {
  OutcomeContactCard,
  OutcomeFlagsCard,
  OutcomeHeader,
  OutcomeNextStepsCard,
  OutcomeWhyCard,
} from './OutcomeCards';

// The gate route this screening actually took, drawn on the full logic tree.
function DecisionTreeCard({ outcome, typeMeta, style }) {
  if (!typeMeta?.logicTree || !outcome?.path?.length) return null;
  return (
    <div className="card" style={{ padding: 16, textAlign: 'left', ...style }}>
      <div
        style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: 0.6, color: 'var(--text-secondary)', marginBottom: 10,
        }}
      >
        Decision path
      </div>
      <LogicTree tree={typeMeta.logicTree} highlightPath={outcome.path} outcome={outcome} />
    </div>
  );
}

function OutcomeCard({ outcome, typeMeta, onRestart }) {
  return (
    <div className="canvas-inner animate-fade-in" style={{ maxWidth: 720 }}>
      <div style={{ padding: '32px 0' }}>
        <OutcomeHeader outcome={outcome} headingLevel={1} style={{ marginBottom: 24 }} />
        <OutcomeContactCard outcome={outcome} style={{ marginBottom: 16 }} />
        <OutcomeFlagsCard outcome={outcome} style={{ marginBottom: 16 }} />
        <DecisionTreeCard outcome={outcome} typeMeta={typeMeta} style={{ marginBottom: 16 }} />
        <OutcomeWhyCard outcome={outcome} style={{ marginBottom: 16 }} />
        <OutcomeNextStepsCard outcome={outcome} style={{ marginBottom: 16 }} />

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {outcome.requestUrl && (
            <a href={outcome.requestUrl} className="btn primary">
              <ArrowUpRight className="w-4 h-4" />
              Open TPRM request
            </a>
          )}
          <button className={`btn ${outcome.requestUrl ? 'outline' : 'primary'}`} onClick={onRestart}>
            <RotateCcw className="w-4 h-4" />
            Start a new intake
          </button>
          <Link to="/" className="btn outline">
            Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function CompletionView({ result, typeMeta, onRestart }) {
  const documents = result.documents || [];
  const outcome = result.outcome || null;

  // Triage outcome (escalate / redirect / stop) — no docs to render.
  if (outcome && outcome.outcome !== 'proceed') {
    return <OutcomeCard outcome={outcome} typeMeta={typeMeta} onRestart={onRestart} />;
  }

  const isBundle = documents.length > 1;
  return (
    <div className="canvas-inner animate-fade-in" style={{ maxWidth: 720 }}>
      <div style={{ padding: '40px 0', textAlign: 'center' }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: 999,
            background: 'var(--surface-green)', color: 'var(--text-green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h1 className="page-title" style={{ marginBottom: 6 }}>
          {isBundle ? 'Your drafts are ready' : 'Your draft is ready'}
        </h1>
        <div className="page-subtitle" style={{ marginBottom: 24 }}>
          {isBundle
            ? `We've created ${documents.length} draft contracts based on your answers. You can download them below. Please reach out to legal if you have any questions.`
            : "We've created a draft contract based on your answers. You can download it below. Please reach out to legal if you have any questions."}
        </div>

        {/* Outcome banner — only present for screening flows that proceeded */}
        {outcome && (
          <div className="callout purple" style={{ marginBottom: 20, textAlign: 'left' }}>
            <div className="callout-body" style={{ fontSize: 12 }}>
              <strong>Path:</strong> {outcome.gate}
              {outcome.contact && <> · <strong>Handoff:</strong> {outcome.contact}</>}
              {outcome.flags?.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {outcome.flags.map((f) => (
                    <span key={f} className="chip code">
                      {f}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DecisionTreeCard outcome={outcome} typeMeta={typeMeta} style={{ marginBottom: 20 }} />

        <div style={{ display: 'grid', gap: 12, marginBottom: 20, textAlign: 'left' }}>
          {documents.map((doc) => (
            <div
              key={doc.contractId}
              className="card"
              style={{ padding: 20 }}
            >
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
                {doc.displayName || typeMeta?.displayName || 'Document'}
              </div>
              <div className="mono" style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 12, wordBreak: 'break-word' }}>
                {doc.filename}
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                {doc.contractFileId && (
                  <a
                    // Direct download — backend streams the bytes from
                    // Drive (or the local fallback) so the browser
                    // saves the file.
                    href={apiUrl(`/contracts/${doc.contractId}/files/${doc.contractFileId}/download`)}
                    download={doc.filename || ''}
                    className="btn primary"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <button className="btn ghost sm" onClick={onRestart}>
          <RotateCcw className="w-3.5 h-3.5" />
          Generate another
        </button>
      </div>
    </div>
  );
}

export { CompletionView };
